import {
  Apploader,
  IsFullAccess,
  useAppDispatch,
  useAppSelector,
  useCheckPreProvisionQuery,
  useGetDemoInstancesQuery,
  useGetEntitlementsQuery,
  userPermissionCheck
} from '@monorepo/shared';
import {
  selectAccountWithPermission,
  selectSystem,
  selectWarnings,
  updateWarnings
} from '@monorepo/shared/store/accountSlice';
import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Divider, Stack, VStack } from 'rsuite';
import { HomeElms, server } from '../../config/const';
import { ProvisionForm } from './Provision';
import { Instance } from './Instance';
import AccountIcon from '../../images/account/account.svg';
import ErrorIcon from '../../images/account/cross-circle.svg';
import ProvisionIcon from '../../images/account/provision.svg';
import { SocWarning } from './Warning';
import { FAZAction } from './FAZAction';
import {
  selectDefaultRegion,
  selectIsInternal,
  selectRegions
} from '@monorepo/shared/store/authSlice';
import { DemoInstance } from './DemoInstance';

export const AccountDetail = () => {
  const dispatch = useAppDispatch();
  const isInternalUser = useAppSelector(selectIsInternal);
  const defaultRegion = useAppSelector(selectDefaultRegion);
  const regions = useAppSelector(selectRegions);
  const account = useAppSelector(selectAccountWithPermission);
  const systemData = useAppSelector(selectSystem);

  const [isAuth, setIsAuth] = useState<string>('');
  const { data: demoInstances, isSuccess: isDemoInstancesSuccess } =
    useGetDemoInstancesQuery(
      { server: server },
      { skip: !isInternalUser || isAuth !== 'failed' }
    );
  const [hasInstance, setHasInstance] = useState<boolean>(false);
  useState<boolean>(false);
  const [provisionObj, setProvisionObj] = useState<Record<string, any>>({});

  const [hasTicket, setHasTicket] = useState<boolean>(false);
  const [hasPreInst, setHasPreInst] = useState<boolean>(false);
  const [isMaster, setIsMaster] = useState<boolean>(false);
  const [isFullAccess, setIsFullAccess] = useState<boolean>(false);
  const [preTicket, setPreTicket] = useState<Record<string, any>>({});
  const [defaultRegionObj, setDefaultRegionObj] = useState<Record<string, any>>(
    {}
  );
  const [preInstanceList, setPreInstanceList] = useState<
    Array<Record<string, any>>
  >([]);

  const [demoInst, setDemoInst] = useState<Record<string, any>>({});

  const warnings = useAppSelector(selectWarnings);
  const {
    data: assets,
    isError,
    isSuccess: isAssetSuccess,
    isLoading: isAssetLoading,
    isFetching
  } = useGetEntitlementsQuery(
    {
      server: server,
      ...account
    },
    {
      skip: !account?.account_id || !account?.portals
    }
  );

  const {
    data: preProvisionCheck,
    isSuccess: isPreProvisionSuccess,
    isLoading: isPreProvisionLoading
  } = useCheckPreProvisionQuery(
    {
      account_id: account?.account_id || '',
      region: regions[defaultRegion?.alias]
    },
    {
      skip:
        hasInstance ||
        account?.org_id ||
        !account?.account_id ||
        !defaultRegionObj?.token ||
        !account?.portals ||
        !regions[defaultRegion?.alias]?.token
    }
  );

  useEffect(() => {
    const region = account?.region || [];
    const hasInst =
      (Array.isArray(region) &&
        region.some((o: Record<string, any>) => o?.url)) ||
      region?.url;

    setHasInstance(!!hasInst);

    setIsMaster(account?.user_type === 'Master');
    setIsFullAccess(IsFullAccess(account, HomeElms?.fcName));
    // reset warnings when account changes
    dispatch(updateWarnings([]));
  }, [account]);

  useEffect(() => {
    const obj = regions[defaultRegion?.alias];
    setDefaultRegionObj(obj);
  }, [regions, defaultRegion]);

  useEffect(() => {
    if (isPreProvisionSuccess && preProvisionCheck.type) {
      if (preProvisionCheck.type === 'EXIST_TICKET') {
        setHasTicket(true);
        setPreTicket({
          type: preProvisionCheck.ticket_type,
          status: preProvisionCheck.ticket_status,
          ctime: preProvisionCheck.ticket_ctime,
          detail: preProvisionCheck.ticket_desc
        });
      } else if (preProvisionCheck.type === 'EXIST_INSTANCE') {
        setHasPreInst(true);
        setPreInstanceList(preProvisionCheck.instance_list);
      }
    } else {
      setHasTicket(false);
      setHasPreInst(false);
      setPreTicket({});
    }
  }, [preProvisionCheck]);

  useEffect(() => {
    if ((account?.account_id && account.portals) || !!account.group_id) {
      const isAuthPassed =
        !!account.group_id || userPermissionCheck(account, HomeElms?.fcName);
      setIsAuth(isAuthPassed ? 'passed' : 'failed');
      if (isAssetSuccess && (!account?.region || !account?.region?.length)) {
        if (!assets?.entitlements?.length) {
          setIsAuth('failed');
        }
      }
      if (assets?.used_sn_list?.length) {
        const obj = assets?.used_sn_list.find(
          (o: Record<string, any>) => o?.sn && !o?.instance_id
        );
        if (obj) {
          setProvisionObj({ ...obj });
          setHasInstance(false);
        }
      }
    }
  }, [assets, account]);

  useEffect(() => {
    if (isAuth === 'failed' && account?.account_id) {
      if (account?.user_auth_url) {
        console.info(
          'Account does not have permission, redirecting to user auth URL...',
          account.user_auth_url
        );
        window.location.href = account.user_auth_url;
      } else if (isDemoInstancesSuccess && account.user_type === 'Master') {
        console.info('demo instances', demoInstances);
        const demoObj =
          demoInstances?.find((o: Record<string, any>) => o.is_default) ||
          demoInstances[0];
        if (demoObj) setDemoInst(demoObj);
      }
    }
  }, [isAuth, account, demoInstances, isDemoInstancesSuccess]);

  const getAccountContent = useCallback(() => {
    return (
      <Stack spacing={'30px'} alignItems="flex-start">
        <Stack.Item>
          <img src={AccountIcon} className="service-icon" />
        </Stack.Item>
        <Stack.Item flex={1} className="info-content">
          <div className="service-title">{' Account Information '}</div>
          <Stack className="info-row">
            <Stack.Item className="info-label">{'Account ID'}</Stack.Item>
            <Stack.Item>{account?.account_id}</Stack.Item>
          </Stack>
          {account?.master_user_name && (
            <Stack className="info-row">
              <Stack.Item className="info-label">{'Owner'}</Stack.Item>
              <Stack.Item>{account?.master_user_name}</Stack.Item>
            </Stack>
          )}
          {account?.account_company && (
            <Stack className="info-row">
              <Stack.Item className="info-label">{'Company'}</Stack.Item>
              <Stack.Item>{account?.account_company}</Stack.Item>
            </Stack>
          )}
          {account?.account_email && (
            <Stack className="info-row">
              <Stack.Item className="info-label">{'Account Email'}</Stack.Item>
              <Stack.Item>{account?.account_email}</Stack.Item>
            </Stack>
          )}
          {account?.account_company && (
            <Stack className="info-row">
              <Stack.Item className="info-label">{'Company'}</Stack.Item>
              <Stack.Item>{account?.account_company}</Stack.Item>
            </Stack>
          )}
        </Stack.Item>
      </Stack>
    );
  }, [account]);

  return (
    <div
      className={`${
        warnings?.length
          ? 'account-detail-container has-warnings'
          : 'account-detail-container'
      }`}
    >
      <div className="warning-container">
        {!account?.is_adom && warnings?.length ? (
          warnings.map((o, i) => (
            <>
              <SocWarning type={o.type} message={o.message} />
            </>
          ))
        ) : (
          <></>
        )}
      </div>
      {isAuth === 'passed' ? (
        <>
          {hasInstance ? (
            <div className="account-info-container">
              {getAccountContent()}
              <Divider style={{ background: '#3a3a3a' }} />
              <Instance />
            </div>
          ) : account.group_id ||
            (!isMaster &&
              !(systemData?.enable_iam_provision && isFullAccess)) ? (
            getAccountContent()
          ) : (
            <>
              {hasTicket ? (
                <TicketContent
                  data={preTicket?.detail?.components}
                  isProvisionFailure={preTicket?.type === 'PROVISION_FAILURE'}
                />
              ) : hasPreInst ? (
                <FAZAction
                  region={regions[defaultRegion?.alias]}
                  account={account}
                  instanceList={preInstanceList}
                  callBack={() => setHasTicket(true)}
                />
              ) : (
                // ) : hasPreProvision ? (
                //   getProvisionFailed()
                <ProvisionForm
                  sn={provisionObj?.sn}
                  region_alias={provisionObj?.region_alias}
                />
              )}
            </>
          )}
        </>
      ) : isAuth === 'failed' ? (
        <AuthFailed
          account={account}
          isInternal={isInternalUser}
          demoInstance={demoInst}
        />
      ) : (
        <Apploader labelText={'loading...'} />
      )}
    </div>
  );
};

interface ITicketProps {
  data: Record<string, any>[];
  isProvisionFailure?: boolean;
}
export const TicketContent = ({ data, isProvisionFailure }: ITicketProps) => {
  const instanceData = data || [];
  return (
    <>
      <Stack
        spacing={'30px'}
        className="provision-container"
        alignItems="flex-start"
      >
        <Stack.Item>
          <img src={ProvisionIcon} className="service-icon" />
        </Stack.Item>
        <Stack.Item flex={1} className="info-content">
          <div className="service-title">{' Provision Service '}</div>
          {isProvisionFailure ? (
            <ProvisionFailureTicket />
          ) : (
            <>
              <div className="info-row" style={{ color: '#B0B0B0' }}>
                <div>{'Your Provision request has been submitted.'}</div>
                <div>
                  {
                    'Our team is now processing it and will notify you as soon as the service is ready.'
                  }
                </div>
                <div>
                  {
                    'Note: Further provisioning actions are temporarily disabled while this request is active.'
                  }
                </div>
                <div className="instances-cards">
                  {instanceData?.length ? (
                    instanceData.map(
                      (item: Record<string, any>, key: number) => (
                        <Card
                          size="md"
                          key={key}
                          style={{
                            cursor: 'pointer',
                            margin: '10px 0px',
                            borderRadius: '0px'
                          }}
                        >
                          <Card.Header as="h6">{item.name}</Card.Header>
                          <Card.Body>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              {item.host ? (
                                <div>
                                  <span style={{ fontWeight: 'bold' }}>
                                    {'Instance URL:'}
                                  </span>{' '}
                                  <span
                                    style={{
                                      fontSize: '0.9em',
                                      opacity: '0.9'
                                    }}
                                  >
                                    {item.host}
                                  </span>
                                </div>
                              ) : (
                                <div>{'Provision task'}</div>
                              )}
                              <div
                                style={{
                                  fontSize: '1.1em',
                                  fontWeight: 'bold'
                                }}
                              >{`${item.action || 'Failed'}`}</div>
                            </div>
                            <div style={{ color: 'lightblue' }}>
                              {item.error_desc ? (
                                <div>
                                  {item.error_code
                                    ? `[${item.error_code}] ${item.error_desc}`
                                    : item.error_desc}
                                </div>
                              ) : (
                                <></>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      )
                    )
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </>
          )}
        </Stack.Item>
      </Stack>
    </>
  );
};

export const ProvisionFailureTicket = () => {
  return (
    <>
      <div className="info-row" style={{ color: '#B0B0B0' }}>
        <div>
          {
            'A component in your environment encountered an error during provisioning.'
          }
        </div>
        <div>
          {
            'Our Operations Team has been alerted and is currently investigating the issue.'
          }
        </div>
        <div>
          {
            'Access to this service is restricted until the fix is deployed to ensure system stability.'
          }
        </div>
        <div>{'We will notify you as soon as service is restored.'}</div>
      </div>
    </>
  );
};

interface IAuthFailedProps {
  account: Record<string, any>;
  isInternal: boolean;
  demoInstance: Record<string, any>;
}
export const AuthFailed = ({
  isInternal,
  demoInstance,
  account
}: IAuthFailedProps) => {
  return (
    <>
      <VStack>
        <div className="account-info-container">
          <Stack
            spacing={'30px'}
            alignItems="flex-start"
            className="account-info-container"
          >
            <Stack.Item>
              <img src={ErrorIcon} className="service-icon" />
            </Stack.Item>
            <Stack.Item flex={1}>
              <div
                className="info-row"
                style={{ maxWidth: '400px', color: '#B0B0B0' }}
              >
                <div
                  className="service-title"
                  style={{
                    marginTop: '10px',
                    color: '#B0B0B0'
                  }}
                >
                  {'No FortiSOC Subscription'}
                </div>
                <p className="info-row">
                  {'Your account does not have a valid FortiSOC subscription.'}
                  <br />
                  {'Please purchase a subscription to continue.'}
                </p>
                <Button
                  appearance="ghost"
                  href="/cred/ou"
                  target="_self"
                  style={{
                    marginTop: '20px',
                    marginBottom: '40px'
                  }}
                >
                  {'Back'}
                </Button>
                {isInternal &&
                account?.user_type === 'Master' &&
                demoInstance?.region_url ? (
                  <Divider
                    size="xs"
                    label={
                      <span
                        style={{
                          fontSize: '12px',
                          opacity: 0.4
                        }}
                      >
                        OR
                      </span>
                    }
                  />
                ) : (
                  <></>
                )}
              </div>
            </Stack.Item>
          </Stack>
        </div>
        {isInternal &&
        account?.user_type === 'Master' &&
        demoInstance?.region_url ? (
          <DemoInstance instance={demoInstance} />
        ) : (
          <div style={{ height: '150px' }}></div>
        )}
      </VStack>
    </>
  );
};
